import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {HolidaySearchFilterService} from '../../services/holiday-search-filter.service';
import {Observable, Subject} from 'rxjs';
import {HolidayFilters} from '../../interfaces/holiday-filters';
import {ChangeContext, Options} from '@angular-slider/ngx-slider';
import {debounceTime, distinctUntilChanged, map, takeUntil} from 'rxjs/operators';
import {HolidayAccomodation} from '../../interfaces/holiday-accomodation';
import {NgbTypeaheadSelectItemEvent} from '@ng-bootstrap/ng-bootstrap';
import {FilterElement} from '../../../../../shared/interfaces/filter-element';
import numberToArray from '../../../../../../utils/number-to-array';
import {PostMessageService} from '../../../../../shared/services/post-message.service';
import { CmsService } from 'src/app/modules/cms/services/cms.service';
import {Networks} from '../../../../../shared/models/networks.enum';
import fixArray from '../../../../../../utils/fixArray';

@Component({
  selector: 'app-holiday-search-filters',
  templateUrl: './holiday-search-filters.component.html',
  styleUrls: ['./holiday-search-filters.component.scss']
})
export class HolidaySearchFiltersComponent implements OnInit, OnDestroy {
  private _destroy$: Subject<boolean> = new Subject();

  @Input() set holidays$(hol) {
    this.filterUpdated = false;
    this.updateFilters(hol);
    this.holidaysObs$ = hol;
  };
  @Output() applyFilterEvent = new EventEmitter<boolean>();

  filters: HolidayFilters;
  sliderRefresh: EventEmitter<void> = new EventEmitter<void>();
  priceSliderOptions: Options = {
    floor: 0,
    ceil: 0
  };
  selectedMin = 0;
  selectedMax = 0;
  filteredAccomodation: FilterElement<HolidayAccomodation>;
  isB2B$: Observable<boolean>;

  public brand: any;
  public networks = Networks;
  private holidaysObs$;
  private filterUpdated = false;

  public hasFiltersSelected = false;
  public hasAccomodationSelected = false;

  constructor(private _postMessageService: PostMessageService,
              private _holidaySearchFilterService: HolidaySearchFilterService,
              private _cmsService: CmsService) {
    this.isB2B$ = this._postMessageService.isB2B();
    this.filters = this._holidaySearchFilterService.filters;
  }

  ngOnInit(): void {
    this._cmsService.brandConfig$.pipe(takeUntil(this._destroy$)).subscribe(brand => {
      if (brand) this.brand = brand;
    });
    this._holidaySearchFilterService.updateTrigger.pipe(takeUntil(this._destroy$)).subscribe(e => {
      if (e != 0 && !this.filterUpdated) {
        console.log("Set filters after parity rate check");
        this.updateFilters(this.holidaysObs$);
        this.filterUpdated = true;
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  private updateFilters(hol) {
    this._holidaySearchFilterService.resetFilters();
    this._holidaySearchFilterService.getFilters(hol).pipe(takeUntil(this._destroy$)).subscribe(e => {
      this.priceSliderOptions = Object.assign({}, this.priceSliderOptions, {
        floor: this.filters?.price?.min || 0,
        ceil: this.filters?.price?.max || 0
      });
      this.selectedMin = this.filters?.price?.min || 0;
      this.selectedMax = this.filters?.price?.max || 0;
      this.sliderRefresh.emit();
    });
  }

  numberToArray = numberToArray;

  accomodationSearch = (text$: Observable<string>) => text$.pipe(
    debounceTime(200),
    distinctUntilChanged(),
    map(text =>  text.length > 1 ?
      this.filters.accomodations.filter(e => e.element.value.name.toLowerCase().includes(text.toLowerCase())) : [])
  )

  accomodationFormatter(result: FilterElement<HolidayAccomodation>): string {
    return result.element.value.name;
  }

  accomodationChangeCB($event: NgbTypeaheadSelectItemEvent): void {
    this.filters.accomodations.forEach(e => e.checked = false);
    $event.item.checked = true;
    this.hasAccomodationSelected = true; // mostro la cta di reset dei filtri
    this.applyFilter('accomodation', $event.item.element.id);
  }

  accomodationClearCB(): void {
    this.filteredAccomodation.checked = false;
    this.filteredAccomodation = undefined;
    this.hasAccomodationSelected = false; // nascondo la cta di reset dei filtri
    this.applyFilter('accomodation', null);
  }

  sliderChangeCB($event: ChangeContext): void {
    this.filters.price.selectedMin = $event.value;
    this.filters.price.selectedMax = $event.highValue;
    this.applyFilter('price', null);
  }

  applyFilter(category, id, value?): void {
    if (this.filters[category]) {
      // salvo i filtri della category in una variabile dedicata (effettuo il fix array per inserire l'eventuale oggetto in un array)
      const filters = fixArray(JSON.parse(JSON.stringify(this.filters[category])));

      this.hasFiltersSelected = false;
      for (const filter of filters) {
        // se presente almeno un filtro selezionato, imposto "hasFiltersSelected" a true per far visualizzare la cta di reset filtri
        if (filter.value || filter.checked) {
          this.hasFiltersSelected = true;
          break;
        }
      }
    }

    this.applyFilterEvent.emit(!!value);
    this._holidaySearchFilterService.updateTrigger.next(Math.random());
  }

  /**
   * Effettuo un reset di tutti i filtri (eccetto la slider del prezzo)
   */
  resetFilters(): void {
    // se presente il filtro delle holiday custom, lo resetto
    if (this.filters.customHoliday.visible) {
      this.filters.customHoliday.value = false;
    }

    // se è stato selezionato un accomodation, lo resetto
    if (this.filteredAccomodation) {
      this.filteredAccomodation.checked = false;
      this.filteredAccomodation = undefined;
    }

    // resetto i vari filtri generici del listato
    for (const touroperator of this.filters.touroperators) {
      touroperator.checked = false;
    }
    for (const departureDate of this.filters.departureDate) {
      departureDate.checked = false;
    }
    for (const night of this.filters.nights) {
      night.checked = false;
    }
    for (const category of this.filters.categories) {
      category.checked = false;
    }
    for (const basis of this.filters.basis) {
      basis.checked = false;
    }

    // imposto a false le proprietà legati alla cta del reset dei filtri (in questo caso verrà nascosta)
    this.hasFiltersSelected = false;
    this.hasAccomodationSelected = false;

    // effettuo l'apply in modo da aggiornare il listato
    this.applyFilter(null, null, true);
  }



}
